/**
 * @file    useWidgetDraftSync.ts
 * @scope   SHARED
 * @summary Keeps the widget draft in the global store in sync with the drawer selection
 *          (questions, calculations, trend, decimals, filters and partition).
 * @consumes The selection state owned by the drawer hooks plus the pure adapters injected by it
 * @produces Nothing — it republishes `customWidget` through `setState`.
 * @sideEffects Writes `customWidget` to the global store on every selection change; may restore
 *              the persisted trend when none is selected yet.
 * @notes Extracted verbatim from the "save selection" effect that used to live in DiyDrawer.
 *        Behaviour and dependency array are intentionally identical — this is a move, not a
 *        redesign.
 */
import { useEffect } from 'react';
import { DIYChartOption } from 'constants/chart-selector';
import { Trend, TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';
import { buildPreviewDataSource } from '../utils/dataSourceResolver';
import { chartsWithTrend, dataGridRulesWithTrend } from '../constants/chartCapabilities';

/** Inputs of {@link useWidgetDraftSync}. */
export type UseWidgetDraftSyncParams = {
  /** Chart option currently selected; without it there is nothing to sync. */
  currentAlias: DIYChartOption | undefined;
  /** Widget draft being edited. */
  customWidget: any;
  /** Global store writer. */
  setState: (partial: any) => void;
  /** ENTERPRISE-ONLY: `true` when the selected chart belongs to the Enterprise family. */
  isDiyWidgetEngine: boolean;
  /** Questions picked in the legacy pickers. */
  selectedQuestions: any[] | undefined;
  /** Calculations picked in the legacy pickers. */
  selectedCalculations: any[] | undefined;
  /** Trend currently selected. */
  selectedChartTrend: TrendOptions | undefined;
  /** Replaces the selected trend (used to restore the persisted one). */
  setSelectedChartTrend: (trend: TrendOptions) => void;
  /** Trend options available for the current chart. */
  chartTrend: any[];
  /** Decimal places selected in the options section. */
  decimalPlaces: number;
  /** Hierarchy filter selection. */
  hierarchyFiltersValues: any;
  /** Advanced filter selection. */
  advancedFiltersValues: any;
  /** Date-range selection. */
  selectedFilteringDateRange: any;
  /** ENTERPRISE-ONLY: partition currently focused. */
  selectedPartition: any;
  /** Builds the persisted filter selection. */
  buildCurrentSelection: (isDiyWidgetEngine: boolean) => any;
  /** Resolves which business rule the current selection satisfies. */
  selectRule: (questions: any[], calculations: any[]) => any[];
  /** Resolves the options lookup key for an alias. */
  currentOptionsAlias: (alias?: string) => string | undefined;
  /** Resolves the preview data source. */
  getDataSourceForPreview: (newAlias: string, trend?: any) => any;
  /** Resolves the alias persisted in `customWidgetConfig.chart`. */
  getAlias: () => any;
};

export const useWidgetDraftSync = ({
  currentAlias,
  customWidget,
  setState,
  isDiyWidgetEngine,
  selectedQuestions,
  selectedCalculations,
  selectedChartTrend,
  setSelectedChartTrend,
  chartTrend,
  decimalPlaces,
  hierarchyFiltersValues,
  advancedFiltersValues,
  selectedFilteringDateRange,
  selectedPartition,
  buildCurrentSelection,
  selectRule,
  currentOptionsAlias,
  getDataSourceForPreview,
  getAlias,
}: UseWidgetDraftSyncParams): void => {
  useEffect(() => {
    if (!currentAlias) {
      selectRule([], []);
      return;
    }

    // Hierarchy/advanced selections plus the persisted values, with the date filter stripped for
    // Enterprise widgets.
    const filters = buildCurrentSelection(isDiyWidgetEngine);

    const questions = selectedQuestions
      ? selectedQuestions.map((question) => question.raw)
      : customWidget.customWidgetConfig?.questions;

    const calculations = selectedCalculations
      ? selectedCalculations
      : customWidget.customWidgetConfig?.calculations;

    const newAlias = currentAlias.component || customWidget.alias;
    const persistedTrend = customWidget?.customWidgetConfig?.trend;
    const dataSource = getDataSourceForPreview(newAlias, persistedTrend);
    const newDataSource = buildPreviewDataSource(dataSource, currentAlias, selectedPartition);

    // Only charts declared as trend-capable persist a trend.
    const trend: Trend[] =
      selectedChartTrend?.trend &&
      (chartsWithTrend.includes(customWidget.alias) ||
        dataGridRulesWithTrend.includes(currentOptionsAlias(currentAlias?.alias) as string))
        ? [selectedChartTrend.trend]
        : [];

    // WHY: reopening a saved widget restores its trend before the trend dropdown has a selection.
    if (!selectedChartTrend && persistedTrend && persistedTrend.length === 1) {
      const savedPeriod: string = persistedTrend[0].timePeriodType || '';
      const trendOption = chartTrend.find(
        (option) => String(option.value).toLowerCase() === String(savedPeriod).toLowerCase()
      );
      setSelectedChartTrend({
        ...trendOption,
        trend: {
          ...trendOption?.trend,
          selectedValue: persistedTrend[0].selectedValue,
        },
      } as TrendOptions);
    }

    const selectedRule = selectRule(questions, calculations as any);

    setState({
      customWidget: {
        ...customWidget,
        ...newDataSource,
        isMockData: true,
        customWidgetConfig: {
          ...customWidget.customWidgetConfig,
          chart: getAlias(),
          trend: trend,
          decimalFormat: resolveDecimalFormat(customWidget, decimalPlaces),
          questions: questions || [],
          calculations: calculations || [],
          rules: selectedRule.length ? selectedRule : customWidget.customWidgetConfig?.rules || [],
          filters: filters,
        },
      },
    });
  }, [
    selectedQuestions,
    selectedCalculations,
    selectedChartTrend,
    decimalPlaces,
    hierarchyFiltersValues,
    advancedFiltersValues,
    selectedFilteringDateRange,
    selectedPartition,
  ]);
};

/**
 * Decimal format persisted with the widget.
 * WHY the three-way fallback: the widget option wins when present, the drawer selection is used
 * while editing, and `-1` means "inherit the chart default".
 */
const resolveDecimalFormat = (customWidget: any, decimalPlaces: number): number => {
  if (typeof customWidget.options?.decimalPlaces !== 'undefined')
    return customWidget.options.decimalPlaces;
  return decimalPlaces >= 0 ? decimalPlaces : -1;
};
